import React from 'react'
import { Button, Center, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react'
import Link from 'next/link'
import { range } from 'ramda'
import { gql } from 'urql'
import { Company } from 'entities/company'
import { Pagination } from 'entities/pagination'
import { isAdminOrCompanyOwner } from 'entities/user'
import { routes } from 'routes'
import { isServerSide } from 'lib/env'
import useMe from 'hooks/useMe'
import useQuery from 'hooks/useQuery'
import {
    BlogCard,
    BlogCardData,
    FRAGMENT_BLOG_CARD,
} from 'containers/blogs/components/BlogCard'
import { PageParams } from 'containers/company-articles/hooks/useParams'

export const PAGE_SIZE = 12

export const QUERY_COMPANY_ARTICLES = gql<
    { company: { blogs: { blogs: BlogCardData[]; pagination: Pagination } } },
    {
        companyId?: string
        pageSize?: number
        pageNumber?: number
    }
>`
    ${FRAGMENT_BLOG_CARD}
    query company_blogs($companyId: ID, $pageSize: Int, $pageNumber: Int) {
        company(id: $companyId) {
            id
            blogs(pageSize: $pageSize, pageNumber: $pageNumber) {
                blogs {
                    ...BlogCard
                }
                pagination {
                    count
                    pageNumber
                    pageSize
                    total
                }
            }
        }
    }
`

export const Header = ({ company }: { company?: Pick<Company, 'id' | 'name'> }) => {
    return (
        <Flex justifyContent="space-between" alignItems="center" mt="52px" mb="24px">
            <Heading size="sm" noOfLines={1}>
                All Articles {company?.name && `from ${company.name}`}
            </Heading>
        </Flex>
    )
}

export const NoArticlesPosted = ({
    company,
}: {
    company?: Pick<Company, 'id' | 'name'>
}) => {
    const currentUser = useMe()

    return (
        <Center flexDir={'column'} textAlign="center" minHeight={'300px'}>
            <Heading size={'md'}>{company?.name} has not posted any articles yet</Heading>
            {isAdminOrCompanyOwner(currentUser, company?.id) && (
                <Link href={routes.newArticle()} passHref>
                    <Button as="a" mt="24px">
                        Post an article
                    </Button>
                </Link>
            )}
        </Center>
    )
}

const PageWithArticles = ({
    page,
    company,
    params,
}: {
    page: number
    company?: Pick<Company, 'id' | 'name'>
    params: PageParams
}) => {
    const companyId = company?.id
    const lastPage = params.page
    const isLastPage = page === lastPage
    const shouldPause = !companyId || isServerSide
    const [companyArticlesResult] = useQuery({
        query: QUERY_COMPANY_ARTICLES,
        variables: {
            companyId: companyId,
            pageSize: PAGE_SIZE,
            pageNumber: page,
        },
        pause: shouldPause,
    })

    if (companyArticlesResult.error) {
        return null
    }

    const blogs = companyArticlesResult.data?.company?.blogs?.blogs ?? []
    const loading = companyArticlesResult.fetching || shouldPause
    const pagination = companyArticlesResult.data?.company?.blogs?.pagination ?? undefined

    const hasMoreToFetch = pagination
        ? pagination.total > pagination.pageNumber * pagination.pageSize
        : false
    const showLoadMore = isLastPage && hasMoreToFetch

    if (!loading && blogs.length === 0 && page === 1) {
        return <NoArticlesPosted company={company} />
    }

    return (
        <>
            <SimpleGrid spacing={'36px'} mb="36px" columns={{ base: 1, md: 2, lg: 3 }}>
                {range(0, PAGE_SIZE).map(idx => {
                    if (loading) {
                        return <BlogCard key={idx} />
                    } else if (blogs[idx]) {
                        return <BlogCard blog={blogs[idx]} key={blogs[idx]?.id} />
                    }
                    return null
                })}
            </SimpleGrid>
            {showLoadMore && (
                <Link
                    href={routes.companyArticles({
                        ...params,
                        page: page + 1,
                    })}
                    passHref
                    shallow={true}
                >
                    <Button as="a" variant="secondary" w="100%" size="lg">
                        Show more articles
                    </Button>
                </Link>
            )}
        </>
    )
}

export const Articles = ({
    company,
    params,
}: {
    company?: Pick<Company, 'name' | 'id'>
    params: PageParams
}) => {
    return (
        <>
            <Header company={company} />
            {range(1, params.page + 1).map(p => {
                return (
                    <PageWithArticles
                        key={p}
                        page={p}
                        company={company}
                        params={params}
                    />
                )
            })}
        </>
    )
}
